import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRight, Key, Loader2, Plus, ShieldCheck, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";

import { ApiError } from "~/lib/api";
import { lookupCountry } from "~/lib/countries";
import {
    type LegacyKeyRecord,
    type LicenseRecord,
    useApiKeys,
    useDeleteLegacyKey,
    useIssueApiKey,
    useLegacyKeys,
    useRevokeApiKey,
    useRevokeLegacyKey,
} from "~/lib/licenses";
import { useTenants } from "~/lib/tenants";
import { useUsers } from "~/lib/users";

import { CopyButton } from "./CopyButton";
import { DetectionScopesMatrix } from "./DetectionScopesMatrix";

const modalStyle: React.CSSProperties = { padding: "var(--space-6)" };

interface LicenseDetailDialogProps {
    license: LicenseRecord | null;
    onClose: () => void;
}

export function LicenseDetailDialog({ license, onClose }: LicenseDetailDialogProps): React.ReactElement | null {
    if (!license) return null;
    return <Inner license={license} onClose={onClose} />;
}

function Inner({ license, onClose }: { license: LicenseRecord; onClose: () => void }): React.ReactElement {
    const tenants = useTenants();
    const tenant = tenants.data?.find((t) => t.id === license.tenantId);
    const keys = useApiKeys(license.id);
    const users = useUsers();
    const contactUser = license.primaryContactUserId
        ? users.data?.find((u) => u.id === license.primaryContactUserId)
        : undefined;
    const countryInfo = lookupCountry(license.country);

    const [issueOpen, setIssueOpen] = useState(false);
    const [issueLabel, setIssueLabel] = useState("");
    const [issuedSecret, setIssuedSecret] = useState<{ label: string; secret: string; prefix: string } | null>(null);
    const [issueError, setIssueError] = useState<string | null>(null);

    const issue = useIssueApiKey();
    const revoke = useRevokeApiKey(license.id);

    async function onIssueSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        setIssueError(null);
        if (!issueLabel.trim()) {
            setIssueError("Label required.");
            return;
        }
        try {
            const res = await issue.mutateAsync({ licenseId: license.id, label: issueLabel.trim() });
            setIssuedSecret({ label: res.key.label, secret: res.secret, prefix: res.key.keyPrefix });
            setIssueOpen(false);
            setIssueLabel("");
        } catch (err) {
            setIssueError(err instanceof ApiError ? err.message : "Failed to issue API key.");
        }
    }

    async function doRevoke(keyId: string, label: string): Promise<void> {
        try {
            await revoke.mutateAsync(keyId);
            toast.success(`Revoked “${label}”`);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to revoke key");
        }
    }

    return (
        <>
            <Dialog.Root open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content dialog-content--lg enter-up" style={modalStyle}>
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <div className="stack-xs">
                                <div className="cluster-xs">
                                    <ShieldCheck size={14} className="text-accent" />
                                    <span className="type-caption">License</span>
                                </div>
                                <Dialog.Title className="type-h2">{license.name}</Dialog.Title>
                                <div className="cluster-xs text-12 text-text-muted">
                                    <span>{tenant ? `${tenant.name} (${tenant.slug})` : license.tenantId}</span>
                                    <span>·</span>
                                    <span className="type-mono-sm">{license.productSlug}</span>
                                    <span>·</span>
                                    <span className={`status-pill status-pill--${statusPillClass(license.status)}`}>
                                        {license.status}
                                    </span>
                                </div>
                            </div>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>

                        <section className="card stack-sm" style={{ marginTop: "var(--space-4)" }}>
                            <h3 className="type-h3">Quotas &amp; scope</h3>
                            <dl className="stack-xs text-13" style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "var(--space-1) var(--space-4)" }}>
                                <dt className="text-text-muted">IoC types</dt>
                                <dd>
                                    {license.iocTypes.length === 0 ? (
                                        <span className="text-text-subtle">all granted</span>
                                    ) : (
                                        <span className="cluster-xs">
                                            {license.iocTypes.map((t) => <span key={t} className="chip">{t}</span>)}
                                        </span>
                                    )}
                                </dd>
                                {license.quotaPerDay !== undefined && (
                                    <>
                                        <dt className="text-text-muted">Quota / day</dt>
                                        <dd className="tabular">{license.quotaPerDay.toLocaleString()}</dd>
                                    </>
                                )}
                                {license.quotaTotal !== undefined && (
                                    <>
                                        <dt className="text-text-muted">Quota total</dt>
                                        <dd className="tabular">{license.quotaTotal.toLocaleString()}</dd>
                                    </>
                                )}
                                {license.seats !== undefined && (
                                    <>
                                        <dt className="text-text-muted">Seats</dt>
                                        <dd className="tabular">{license.seats}</dd>
                                    </>
                                )}
                                {license.devices !== undefined && (
                                    <>
                                        <dt className="text-text-muted">Devices</dt>
                                        <dd className="tabular">{license.devices}</dd>
                                    </>
                                )}
                                {license.validFrom && (
                                    <>
                                        <dt className="text-text-muted">Valid from</dt>
                                        <dd>{new Date(license.validFrom).toLocaleString()}</dd>
                                    </>
                                )}
                                {license.expiresAt && (
                                    <>
                                        <dt className="text-text-muted">Expires</dt>
                                        <dd>{new Date(license.expiresAt).toLocaleString()}</dd>
                                    </>
                                )}
                                {license.country && (
                                    <>
                                        <dt className="text-text-muted">Country</dt>
                                        <dd>
                                            {countryInfo
                                                ? `${countryInfo.flag} ${countryInfo.name} (${countryInfo.code})`
                                                : license.country}
                                        </dd>
                                    </>
                                )}
                                {license.primaryContactUserId && (
                                    <>
                                        <dt className="text-text-muted">Primary contact</dt>
                                        <dd>
                                            {contactUser
                                                ? contactUser.email
                                                : license.primaryContactUserId.slice(0, 8)}
                                        </dd>
                                    </>
                                )}
                                {license.notes && (
                                    <>
                                        <dt className="text-text-muted">Notes</dt>
                                        <dd style={{ whiteSpace: "pre-line" }}>{license.notes}</dd>
                                    </>
                                )}
                            </dl>
                        </section>

                        <section className="card stack-sm" style={{ marginTop: "var(--space-4)" }}>
                            <h3 className="type-h3">Detection rule access</h3>
                            {Object.keys(license.detectionScopes ?? {}).length === 0 ? (
                                <span className="text-13 text-text-subtle">
                                    No detection bundle access granted.
                                </span>
                            ) : (
                                <DetectionScopesMatrix
                                    value={license.detectionScopes ?? {}}
                                    readOnly
                                />
                            )}
                        </section>

                        <section className="stack-sm" style={{ marginTop: "var(--space-6)" }}>
                            <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                                <h3 className="type-h3">API keys</h3>
                                <button
                                    type="button"
                                    className="btn btn--secondary btn--sm press-crush"
                                    onClick={() => setIssueOpen(true)}
                                >
                                    <Plus size={14} />
                                    <span>Issue key</span>
                                </button>
                            </div>

                            {keys.isLoading && (
                                <div className="empty-state" style={{ padding: "var(--space-4)" }}>
                                    <Loader2 size={18} className="empty-state__icon animate-spin" aria-hidden />
                                    <div className="empty-state__body">Loading keys…</div>
                                </div>
                            )}
                            {keys.data && keys.data.length === 0 && (
                                <div className="empty-state" style={{ padding: "var(--space-6)" }}>
                                    <Key size={20} className="empty-state__icon" aria-hidden />
                                    <div className="empty-state__title">No API keys yet</div>
                                    <div className="empty-state__body">
                                        Issue one above. The plaintext secret is shown exactly once.
                                    </div>
                                </div>
                            )}
                            {keys.data && keys.data.length > 0 && (
                                <div style={{ overflowX: "auto" }}>
                                    <table className="data-table data-table--dense">
                                        <thead>
                                            <tr>
                                                <th>Label</th>
                                                <th>Prefix</th>
                                                <th>Created</th>
                                                <th>Last used</th>
                                                <th>Status</th>
                                                <th style={{ width: 1 }} aria-label="Actions" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {keys.data.map((k) => {
                                                const revoked = !!k.revokedAt;
                                                return (
                                                    <tr key={k.id}>
                                                        <td className="text-13">{k.label}</td>
                                                        <td className="type-mono-sm">{k.keyPrefix}…</td>
                                                        <td className="text-12 text-text-muted">
                                                            {new Date(k.createdAt).toLocaleDateString()}
                                                        </td>
                                                        <td className="text-12 text-text-muted">
                                                            {k.lastUsedAt
                                                                ? new Date(k.lastUsedAt).toLocaleDateString()
                                                                : "—"}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`status-pill ${revoked ? "status-pill--failed" : "status-pill--live"}`}
                                                            >
                                                                {revoked ? "revoked" : "active"}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: "right" }}>
                                                            <button
                                                                type="button"
                                                                className="btn btn--ghost btn--sm"
                                                                disabled={revoked || revoke.isPending}
                                                                onClick={() => void doRevoke(k.id, k.label)}
                                                                title="Revoke"
                                                                aria-label="Revoke API key"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        <LegacyKeysSection licenseId={license.id} />
                    </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>

            {/* Issue key dialog */}
            <Dialog.Root open={issueOpen} onOpenChange={setIssueOpen}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content enter-up" style={modalStyle}>
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">Issue API key</Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>
                        <Dialog.Description className="text-text-muted text-12" style={{ marginTop: "var(--space-2)" }}>
                            The plaintext key is shown once — copy it before closing the next dialog.
                        </Dialog.Description>
                        <form onSubmit={onIssueSubmit} className="stack" style={{ marginTop: "var(--space-4)" }}>
                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Label</span>
                                <input
                                    autoFocus
                                    className="input"
                                    value={issueLabel}
                                    onChange={(e) => setIssueLabel(e.target.value)}
                                    placeholder="DMX prod, west-1"
                                    required
                                />
                            </label>
                            {issueError && (
                                <div className="chip chip--danger" style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}>
                                    {issueError}
                                </div>
                            )}
                            <button type="submit" className="btn btn--primary press-crush" disabled={issue.isPending}>
                                {issue.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                <span>{issue.isPending ? "Issuing…" : "Issue key"}</span>
                            </button>
                        </form>
                    </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>

            {/* One-time secret reveal */}
            {issuedSecret && (
                <Dialog.Root open={true} onOpenChange={(o) => !o && setIssuedSecret(null)}>
                    <Dialog.Portal>
                        <Dialog.Overlay className="dialog-overlay">
                        <Dialog.Content className="dialog-content dialog-content--md enter-up" style={modalStyle}>
                            <Dialog.Title className="type-h2">API key issued</Dialog.Title>
                            <Dialog.Description className="text-text-muted text-13" style={{ marginTop: "var(--space-2)" }}>
                                Copy the key now. <strong className="text-text">It will never be shown again.</strong>
                                {" "}Only the prefix <code className="type-mono-sm">{issuedSecret.prefix}…</code> is recoverable later.
                            </Dialog.Description>
                            <div
                                className="surface"
                                style={{
                                    padding: "var(--space-3)",
                                    marginTop: "var(--space-4)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "var(--space-3)",
                                    fontFamily: "var(--font-family-mono)",
                                    fontSize: "var(--font-size-12)",
                                }}
                            >
                                <span style={{ flex: 1, wordBreak: "break-all" }}>{issuedSecret.secret}</span>
                                <CopyButton value={issuedSecret.secret} />
                            </div>

                            {/* Phase 2.G — first-time-API-user nudge. The
                                green border picks up the standard success
                                accent so it reads as helpful, not alarming. */}
                            <Link
                                to="/docs/quickstart"
                                onClick={() => setIssuedSecret(null)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "var(--space-2)",
                                    marginTop: "var(--space-4)",
                                    padding: "var(--space-3)",
                                    border: "1px solid var(--color-success)",
                                    borderRadius: "var(--radius-sm)",
                                    background: "var(--color-success-subtle)",
                                    color: "var(--color-text)",
                                    textDecoration: "none",
                                    fontSize: "var(--font-size-13)",
                                }}
                            >
                                <span style={{ flex: 1 }}>
                                    <strong>First time using the API?</strong>{" "}
                                    Open the 3-step quickstart.
                                </span>
                                <ArrowRight size={14} aria-hidden />
                            </Link>

                            <button
                                type="button"
                                className="btn btn--primary press-crush"
                                style={{ marginTop: "var(--space-4)" }}
                                onClick={() => setIssuedSecret(null)}
                            >
                                Done
                            </button>
                        </Dialog.Content>
                        </Dialog.Overlay>
                    </Dialog.Portal>
                </Dialog.Root>
            )}
        </>
    );
}

function statusPillClass(status: LicenseRecord["status"]): string {
    switch (status) {
        case "active":
            return "live";
        case "suspended":
            return "paused";
        case "expired":
            return "queued";
        case "revoked":
            return "failed";
    }
}

// ---------------------------------------------------------------------------
// LegacyKeysSection — V0/V1 portal bridge
// ---------------------------------------------------------------------------
//
// Renders zero-or-more legacy keys attached to this license by the
// import-legacy-licenses CLI. Plaintext is never on the wire — backend
// only has sha256(uuid) + last4, so we render `••••<last4>` chips.
// Operator actions: revoke (sets revoked_at, auth fallback rejects with
// 401) and delete (hard removal — only after the customer rotates to a
// V2-issued key). The section hides itself entirely when there are no
// legacy keys, so customer-facing licenses without imported keys
// don't pick up visual clutter.

function LegacyKeysSection({ licenseId }: { licenseId: string }): React.ReactElement | null {
    const { data, isLoading } = useLegacyKeys(licenseId);
    const revoke = useRevokeLegacyKey(licenseId);
    const del = useDeleteLegacyKey(licenseId);

    async function doRevoke(k: LegacyKeyRecord): Promise<void> {
        if (!confirm(`Revoke legacy key ••••${k.last4}?\n\nThe customer's DMX/DSX deployment using this UUID will start receiving 401. This cannot be undone — they'll need a V2-issued replacement key.`)) {
            return;
        }
        try {
            await revoke.mutateAsync(k.id);
            toast.success(`Revoked legacy key ••••${k.last4}`);
        } catch (e) {
            toast.error(e instanceof ApiError ? e.message : "Revoke failed");
        }
    }
    async function doDelete(k: LegacyKeyRecord): Promise<void> {
        if (!confirm(`Hard-delete legacy key ••••${k.last4}?\n\nThe row is removed from license_legacy_keys. Use this only after the customer is fully migrated to a V2-issued key.`)) {
            return;
        }
        try {
            await del.mutateAsync(k.id);
            toast.success(`Deleted legacy key ••••${k.last4}`);
        } catch (e) {
            toast.error(e instanceof ApiError ? e.message : "Delete failed");
        }
    }

    if (isLoading) {
        return (
            <section className="stack-sm" style={{ marginTop: "var(--space-6)" }}>
                <h3 className="type-h3">Legacy keys</h3>
                <div className="empty-state" style={{ padding: "var(--space-3)" }}>
                    <Loader2 size={16} className="empty-state__icon animate-spin" aria-hidden />
                    <div className="empty-state__body">Loading legacy keys…</div>
                </div>
            </section>
        );
    }

    // Hide the section entirely when no legacy keys exist on this
    // license — keeps non-imported customer dialogs clean.
    if (!data || data.length === 0) {
        return null;
    }

    return (
        <section className="stack-sm" style={{ marginTop: "var(--space-6)" }}>
            <div className="cluster-xs" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
                <h3 className="type-h3">
                    Legacy keys
                    <span className="text-text-muted text-13" style={{ marginLeft: "var(--space-2)", fontWeight: 400 }}>
                        — V0/V1 portal bridge
                    </span>
                </h3>
                <span className="status-pill status-pill--info" style={{ fontSize: 10 }}>
                    {data.length} attached
                </span>
            </div>

            <p className="text-12 text-text-muted" style={{ margin: 0 }}>
                Inherited UUID-shaped keys from the V0/V1 portal. The plaintext was hashed at
                import time and is not retrievable here — only the customer holds it. Use
                Revoke to immediately reject auth without removing the audit row.
            </p>

            <div style={{ overflowX: "auto" }}>
                <table className="data-table data-table--dense">
                    <thead>
                        <tr>
                            <th>Key</th>
                            <th>Source</th>
                            <th>Customer</th>
                            <th>Product</th>
                            <th style={{ whiteSpace: "nowrap" }}>Valid until</th>
                            <th>Status</th>
                            <th style={{ width: 1 }} aria-label="Actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((k) => {
                            const expired = !k.revokedAt && new Date(k.validUntil) < new Date();
                            const inactiveTone = !!k.revokedAt || expired;
                            const statusLabel = k.revokedAt
                                ? "revoked"
                                : expired
                                    ? "expired"
                                    : "active";
                            return (
                                <tr key={k.id}>
                                    <td>
                                        <code
                                            className="type-mono-sm"
                                            style={{
                                                background: "var(--color-surface-muted)",
                                                padding: "2px 6px",
                                                borderRadius: "var(--radius-sm)",
                                                letterSpacing: "0.05em",
                                            }}
                                            title="The full UUID was hashed at import; only last4 is shown."
                                        >
                                            ••••{k.last4}
                                        </code>
                                    </td>
                                    <td className="text-12 text-text-muted">{k.sourceSystem}</td>
                                    <td className="text-12">
                                        {k.legacyUserLabel || "—"}
                                        {k.legacyUserId && (
                                            <div className="text-12 text-text-muted type-mono-sm" style={{ marginTop: 2 }}>
                                                {k.legacyUserId.length > 16 ? `${k.legacyUserId.slice(0, 12)}…` : k.legacyUserId}
                                            </div>
                                        )}
                                    </td>
                                    <td className="text-12 text-text-muted">{k.legacyProduct}</td>
                                    <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                                        {new Date(k.validUntil).toLocaleDateString()}
                                    </td>
                                    <td>
                                        <span
                                            className={`status-pill ${
                                                inactiveTone
                                                    ? "status-pill--failed"
                                                    : "status-pill--live"
                                            }`}
                                        >
                                            {statusLabel}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                        {!k.revokedAt && (
                                            <button
                                                type="button"
                                                className="btn btn--ghost btn--sm"
                                                onClick={() => void doRevoke(k)}
                                                title="Revoke (sets revoked_at)"
                                                aria-label={`Revoke legacy key ${k.last4}`}
                                                disabled={revoke.isPending}
                                            >
                                                <ShieldCheck size={14} />
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn--ghost btn--sm"
                                            onClick={() => void doDelete(k)}
                                            title="Hard-delete row"
                                            aria-label={`Delete legacy key ${k.last4}`}
                                            disabled={del.isPending}
                                            style={{ color: "var(--color-danger)" }}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
